/**
 * MediaPickerModal — pick a Catalog video or Picture and attach it to a tweet.
 *
 * Videos are served via Mux `.mp4` (low quality — stays under X's 512 MB cap).
 * Pictures use their raw `image_url` (Cloudinary hosted).
 *
 * On confirm we call `/api/x/media/upload-url` server-side so the file is
 * downloaded + chunked-uploaded to X. The parent gets the `media_id` back
 * and treats it identically to a device upload.
 */
import React, { useEffect, useState, useMemo } from "react";
import { X as XIcon, Film, Image as ImageIcon, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Mux MP4 quality — "low" ~ 480p; keeps most catalog videos <30 MB and safely
// under X's 512 MB / free-tier bandwidth limits.
const MUX_MP4 = (pid) => `https://stream.mux.com/${pid}/low.mp4`;
const MUX_POSTER = (pid) => `https://image.mux.com/${pid}/thumbnail.jpg?width=320`;

export default function MediaPickerModal({ open, onClose, onPicked, attachedCount = 0, max = 4 }) {
  const [tab, setTab] = useState("videos");
  const [videos, setVideos] = useState([]);
  const [pictures, setPictures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/api/videos/?limit=200").catch(() => ({ data: [] })),
      api.get("/api/pictures/?limit=200").catch(() => ({ data: [] })),
    ]).then(([v, p]) => {
      const vids = (Array.isArray(v.data) ? v.data : v.data?.videos || [])
        .filter(x => x.mux_playback_id || x.trailer_mux_playback_id || x.of_cutter_mux_playback_id);
      const pics = (Array.isArray(p.data) ? p.data : p.data?.pictures || [])
        .filter(x => x.image_url);
      setVideos(vids);
      setPictures(pics);
    }).finally(() => setLoading(false));
  }, [open]);

  const canAdd = attachedCount < max;

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    const list = tab === "videos" ? videos : pictures;
    if (!q) return list;
    return list.filter(x => (x.title || "").toLowerCase().includes(q));
  }, [tab, videos, pictures, query]);

  const pickVideo = async (v) => {
    if (!canAdd) { toast.error(`Max ${max} media per tweet`); return; }
    // Prefer trailer (short) > cutter (short) > main (full)
    const pid = v.trailer_mux_playback_id || v.of_cutter_mux_playback_id || v.mux_playback_id;
    const thumb = MUX_POSTER(pid);
    await uploadUrl("video", v.id, thumb, "video/mp4", v.title || "catalog-video");
  };

  const pickPicture = async (p) => {
    if (!canAdd) { toast.error(`Max ${max} media per tweet`); return; }
    await uploadUrl("picture", p.id, p.image_url, "image/jpeg", p.title || "catalog-picture");
  };

  const uploadUrl = async (catalog_type, catalog_id, thumb, mime, label) => {
    const uploadKey = `${catalog_type}:${catalog_id}`;
    setUploadingId(uploadKey);
    try {
      const r = await api.post("/api/x-studio/media/upload", { catalog_type, catalog_id });
      onPicked({ ...r.data, thumb, mime, label });
      toast.success(`✓ Attached: ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Media attach failed");
    } finally {
      setUploadingId(null);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="x-media-picker-modal">
      <div className="w-full max-w-5xl rounded-lg overflow-hidden flex flex-col"
        style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)", maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b"
          style={{ borderColor: "var(--gio-border)" }}>
          <div className="flex items-center gap-3">
            <div className="text-[14px] font-bold" style={{ color: "var(--gio-text)" }}>
              Attach from Catalog
            </div>
            <div className="text-[11px]" style={{ color: "var(--gio-muted-2)" }}>
              {attachedCount}/{max} attached · uploads via server-side to X
            </div>
          </div>
          <button onClick={onClose} data-testid="x-media-picker-close"
            className="p-1 rounded hover:bg-[var(--gio-surface-hi)]" style={{ color: "var(--gio-muted-2)" }}>
            <XIcon size={18} />
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-2 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <button onClick={() => setTab("videos")} data-testid="x-picker-tab-videos"
            className="px-3 py-1.5 rounded-md text-[12px] inline-flex items-center gap-1.5"
            style={{
              background: tab === "videos" ? "var(--gio-primary)" : "transparent",
              color: tab === "videos" ? "#000" : "var(--gio-muted-2)",
              border: `1px solid ${tab === "videos" ? "var(--gio-primary)" : "var(--gio-border)"}`,
            }}>
            <Film size={13} /> Videos ({videos.length})
          </button>
          <button onClick={() => setTab("pictures")} data-testid="x-picker-tab-pictures"
            className="px-3 py-1.5 rounded-md text-[12px] inline-flex items-center gap-1.5"
            style={{
              background: tab === "pictures" ? "var(--gio-primary)" : "transparent",
              color: tab === "pictures" ? "#000" : "var(--gio-muted-2)",
              border: `1px solid ${tab === "pictures" ? "var(--gio-primary)" : "var(--gio-border)"}`,
            }}>
            <ImageIcon size={13} /> Pictures ({pictures.length})
          </button>
          <input
            type="text"
            placeholder="Search by title…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="x-picker-search"
            className="ml-auto gio-input text-[12px]"
            style={{ maxWidth: 240 }}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="text-center py-8" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-8 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>
              {tab === "videos"
                ? "No videos with Mux playback available."
                : "No pictures found."
              }
            </div>
          )}
          {!loading && tab === "videos" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map(v => {
                const pid = v.trailer_mux_playback_id || v.of_cutter_mux_playback_id || v.mux_playback_id;
                const isUploading = uploadingId === `video:${v.id}`;
                const flavor = v.trailer_mux_playback_id ? "trailer"
                             : v.of_cutter_mux_playback_id ? "cutter" : "full";
                return (
                  <button key={v.id} onClick={() => pickVideo(v)}
                    disabled={isUploading || !canAdd}
                    data-testid={`x-picker-video-${v.id}`}
                    className="text-left rounded-lg overflow-hidden border relative hover:-translate-y-0.5 transition disabled:opacity-40"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
                    <div className="aspect-video bg-black relative">
                      <img src={MUX_POSTER(pid)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      {isUploading && (
                        <div className="absolute inset-0 flex items-center justify-center"
                          style={{ background: "rgba(0,0,0,0.75)" }}>
                          <Loader2 size={20} className="animate-spin" style={{ color: "var(--gio-primary)" }} />
                        </div>
                      )}
                      <span className="absolute top-1 left-1 text-[8px] font-mono-ui uppercase font-bold px-1.5 py-0.5 rounded"
                        style={{ background: "rgba(0,0,0,0.75)", color: "var(--gio-primary)" }}>
                        {flavor}
                      </span>
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-[11px] font-semibold line-clamp-2" style={{ color: "var(--gio-text)" }}>
                        {v.title || "untitled"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {!loading && tab === "pictures" && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {filtered.map(p => {
                const isUploading = uploadingId === p.image_url;
                return (
                  <button key={p.id} onClick={() => pickPicture(p)}
                    disabled={isUploading || !canAdd}
                    data-testid={`x-picker-picture-${p.id}`}
                    className="aspect-square rounded overflow-hidden border relative hover:scale-105 transition disabled:opacity-40"
                    style={{ borderColor: "var(--gio-border)" }}
                    title={p.title}>
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    {isUploading && (
                      <div className="absolute inset-0 flex items-center justify-center"
                        style={{ background: "rgba(0,0,0,0.75)" }}>
                        <Loader2 size={16} className="animate-spin" style={{ color: "var(--gio-primary)" }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
